import { useQuery } from "@tanstack/react-query";
import type { Edge, Node } from "@xyflow/react";
import { OrganizationStructureApiClient } from "src/Pages/Company/OrganizationStructure/apiClient/OrganizationStructureApiClient";
import { PresenceInformation } from "@benefit-systems/hr-tools-ui";
// eslint-disable-next-line
import { getEmployeeInitialDataWorker } from "../workers/getOrgchartWebWorker";
import useOrgchartEmployeesStore from "./store";

let InitialDataModificationWorker: Worker;

interface IWebWorker {
  initialNodes: Node[];
  initialEdges: Edge[];
}

// Response from /v1/companies/{companyId}/structures/{structureId}/employees
export interface EmployeesOrgchartDTO {
  id: string;
  fk: string;
  name: string;
  initials: string;
  avatar: string | null;
  avatarImageSet: unknown;
  position: { id: string; label: string } | null;
  area: { id: string; label: string } | null;
  additionalDisplayArea: { id: string; label: string } | null;
  isSupervisor: boolean | null;
  supervisorId: string | null;
  presenceInformation: PresenceInformation | null;
}

export interface PagedResponse<T> {
  content: T[];
  size: number;
  nextCursor: string | null;
  totalElements: number;
}

export interface IEmployeeInOrgchartChild {
  employeeId: string;
  name: string;
  initials: string;
  position?: string;
  area?: { id: string; label: string };
  isSupervisor: boolean;
  subordinateCount: number;
  children: IEmployeeInOrgchartChild[];
}

export interface IEmployeeInOrgchartData {
  structureName: string;
  children: IEmployeeInOrgchartChild[];
}

/**
 * Fetch employees for the orgchart using the new paginated endpoint.
 * Replaces the deprecated /employees-org-chart endpoint.
 */
const fetchEmployees = async (
  companyId: string,
  structureId: string,
  cursor?: string,
  nodeId?: string
): Promise<PagedResponse<EmployeesOrgchartDTO>> => {
  const params = new URLSearchParams({ size: "5" });

  if (nodeId && nodeId !== "root") {
    params.append("filters", `(supervisorId==${nodeId})`);
  } else {
    params.append("filters", "(supervisorId=null=)");
  }

  if (cursor) params.append("cursor", cursor);

  const { data } = await OrganizationStructureApiClient.get(
    `v1/companies/${companyId}/structures/${structureId}/employees?${params.toString()}`
  );
  return data;
};

/**
 * Hook that fetches initial employees orgchart data using the new /employees endpoint.
 * Processes the paginated response through the web worker to build nodes and edges.
 */
export const useEmployeesOrgchartData = ({
  companyId,
  structureId,
}: {
  companyId: string;
  structureId: string;
}) =>
  useQuery({
    queryKey: ["employeesOrgchart", companyId, structureId],
    enabled: !!companyId && !!structureId,
    queryFn: async () => {
      const data = await fetchEmployees(companyId, structureId);

      // Fetch structure info for root node name (like dashboard's baseEmployeeOrganizationStructureInformation)
      const { data: structureInfo } = await OrganizationStructureApiClient.get(
        `/v1/companies/${companyId}/structures/${structureId}`
      );

      return new Promise<PagedResponse<EmployeesOrgchartDTO>>((resolve) => {
        if (!InitialDataModificationWorker) {
          InitialDataModificationWorker = getEmployeeInitialDataWorker();
        }
        InitialDataModificationWorker.postMessage({
          orgchartData: data,
          structureName: structureInfo?.name || "Struktura",
          structureAvatar: structureInfo?.avatarImageSet?.original || null,
        });

        InitialDataModificationWorker.onmessage = (
          event: MessageEvent<IWebWorker>
        ) => {
          event.data.initialNodes.forEach((node) => {
            if (node.data.isSupervisor) {
              useOrgchartEmployeesStore
                .getState()
                .setChildrenCollapsedStatus(
                  new Map(
                    useOrgchartEmployeesStore.getState().childrenCollapsedStatus
                  ).set(node.id, node.id !== "root")
                );
            }
          });

          useOrgchartEmployeesStore
            .getState()
            .setAllNodes(event.data.initialNodes);
          if (!useOrgchartEmployeesStore.getState().searchedNodeId) {
            useOrgchartEmployeesStore
              .getState()
              .setNodes(event.data.initialNodes);
            useOrgchartEmployeesStore
              .getState()
              .setEdges(event.data.initialEdges);
          }

          // Store root subordinates count if nextCursor exists (only once)
          if (
            useOrgchartEmployeesStore.getState().rootSubordinatesCount ===
              null &&
            data.nextCursor
          ) {
            useOrgchartEmployeesStore.getState().rootSubordinatesCount =
              data.totalElements;
          }

          // Store nextCursor for root if present
          if (data.nextCursor) {
            useOrgchartEmployeesStore
              .getState()
              .setPaginationCursor("root", data.nextCursor);
          }

          resolve(data);
        };
      });
    },
  });

/**
 * Fetch more employees for a given supervisor (pagination).
 */
export const fetchEmployeeOrgchart = async (
  companyId: string,
  structureId: string,
  cursor?: string,
  nodeId?: string
): Promise<PagedResponse<EmployeesOrgchartDTO>> =>
  fetchEmployees(companyId, structureId, cursor, nodeId);

/**
 * Fetch employee hierarchy (path from root to searched employee).
 */
export const fetchEmployeeInOrgchart = async (
  companyId: string,
  structureId: string,
  employeeId: string
): Promise<IEmployeeInOrgchartData> => {
  const { data } = await OrganizationStructureApiClient.get(
    `v1/companies/${companyId}/structures/${structureId}/employees/${employeeId}/hierarchy`
  );
  return data;
};

/**
 * Fetch employees excluding specific IDs (used after search to avoid duplicates).
 */
export const fetchEmployeeOrgchartWithExclusions = async (
  companyId: string,
  structureId: string,
  nodeId: string,
  excludeIds: string[],
  cursor?: string
): Promise<PagedResponse<EmployeesOrgchartDTO>> => {
  const params = new URLSearchParams({ size: "5" });

  const filters: string[] = [
    nodeId === "root" ? `(supervisorId=null=)` : `(supervisorId==${nodeId})`,
  ];

  if (excludeIds.length > 0) {
    filters.push(`(employeeId=out=(${excludeIds.join(",")}))`);
  }

  params.append("filters", filters.join(";"));

  if (cursor) {
    params.append("cursor", cursor);
  }

  const { data } = await OrganizationStructureApiClient.get(
    `v1/companies/${companyId}/structures/${structureId}/employees?${params.toString()}`
  );
  return data;
};

/**
 * Process paginated employee response into ReactFlow nodes and edges.
 */
export const processChildrenNodes = (
  data: PagedResponse<EmployeesOrgchartDTO>,
  parentId: string
): { nodes: Node[]; edges: Edge[] } => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const defaultNodeValues = {
    position: { x: 0, y: 0 },
    type: "employeeNode",
  };

  data.content.forEach((employee) => {
    nodes.push({
      id: employee.id,
      data: {
        ...employee,
        employeeId: employee.id,
        hasChildren: !!employee.isSupervisor,
        parentId,
        supervisorId: parentId,
      },
      ...defaultNodeValues,
    });
    edges.push({
      id: `${parentId}-${employee.id}`,
      source: parentId,
      target: employee.id,
      type: "customEdge",
      hidden: false,
    });
  });

  // Store nextCursor if present
  if (data.nextCursor) {
    useOrgchartEmployeesStore
      .getState()
      .setPaginationCursor(parentId, data.nextCursor);
  }

  return { nodes, edges };
};
